
import {
  defineComponent, reactive, watch, computed,
} from 'vue';
import Icone from '@/core/components/Icone.vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import ServicoNotaFiscal from '@/servicos/Fiscal/ServicoNotaFiscal';
import UtilitarioDispositivo from '@/core/utilitarios/UtilitarioDispositivo';
import { IFormaPagamentoTipoDocumentoFinanceiro } from '@/models/Entidades/Cadastros/Financeiro/IFormaPagamento';
import { IOption } from '@/core/models/AntDesign/IOption';
import SelecionarTipoDocumentoFinanceiro from '@/components/Cadastros/Financeiro/SelecionarTipoDocumentoFinanceiro.vue';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import UtilitarioMatematico from '@/core/utilitarios/UtilitarioMatematico';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import UtilitarioFinanceiro from '@/core/utilitarios/UtilitarioFinanceiro';
import { useRequisicaoAutorizacaoBase } from '@/composables/MeuSistema/RequisicaoAutorizacaoBase';
import RequisitarAutorizacaoModal from '@/components/MeuSistema/ControleAutorizacoes/RequisitarAutorizacaoModal.vue';
import PedidoCompraDetalhePagamentoOutros from './PedidoCompraDetalhePagamentoOutros.vue';
import PedidoCompraDetalhePagamentoParcelado from './PedidoCompraDetalhePagamentoParcelado.vue';
import { IPedidoCompra, IPedidoCompraDetalhePagamento } from '@/models/Entidades/Compras/IPedidoCompra';
import ServicoFormaPagamento from '@/servicos/Cadastros/Financeiro/ServicoFormaPagamento';

export default defineComponent({
  name: 'PedidoCompraDetalhePagamentoModal',
  props: {
    visivel: {
      type: Boolean,
    },
    editavel: {
      type: Boolean,
      required: true,
    },
    pedidoCompra: {
      type: Object as () => IPedidoCompra,
      required: true,
    },
    tiposDocumentos: {
      type: Array as () => number[],
      required: true,
    },
    detalhePagamento: {
      type: Object as () => IPedidoCompraDetalhePagamento,
      required: true,
    },
  },
  components: {
    Icone,
    PedidoCompraDetalhePagamentoOutros,
    PedidoCompraDetalhePagamentoParcelado,
    SelecionarTipoDocumentoFinanceiro,
    RequisitarAutorizacaoModal,
  },
  emits: ['update:visivel', 'update:pedidoCompra', 'confirmar'],
  setup(props, { emit }) {
    const servicoNotaFiscal = new ServicoNotaFiscal();
    const servicoFormaPagamento = new ServicoFormaPagamento();
    servicoFormaPagamento.requisicaoSistema();
    servicoNotaFiscal.requisicaoSistema();
    const { telaBase, apresentarMensagemAlerta } = useTelaBase();
    const { modalBase } = useModalBase(props, emit);
    const { requisicaoAutorizacaoBase } = useRequisicaoAutorizacaoBase();

    const state = reactive({
      tiposDocumentosDisponiveis: [] as number[],
      parcelasDisponiveis: [] as IOption[],
      detalhePagamento: {} as IPedidoCompraDetalhePagamento,
      tipoDocumentoSelecionado: {} as IFormaPagamentoTipoDocumentoFinanceiro,
      qtdParcelasSelecionada: 0,
      valorParcelado: 0,
      valorRestante: 0,
      telaMobile: false,
      codigoPlanoConta: 0,
      nomePlanoConta: '',
    });

    const computedPedidoCompra = computed({
      get: () => props.pedidoCompra,
      set: (val: IPedidoCompra) => {
        emit('update:pedidoCompra', val);
      },
    });

    async function limparTela() {
      state.detalhePagamento = {} as IPedidoCompraDetalhePagamento;
      state.detalhePagamento.alterado = false;
      state.detalhePagamento.valor = 0;
      state.tipoDocumentoSelecionado = {} as IFormaPagamentoTipoDocumentoFinanceiro;
      state.parcelasDisponiveis = [] as IOption[];
      state.qtdParcelasSelecionada = 0;
      state.valorParcelado = 0;
      state.valorRestante = 0;
    }

    function gerarNumeroTitulo() {
      let numero = '';
      let qtdParcela = 0;
      const sigla = state.tipoDocumentoSelecionado.siglaTipoDocumentoFinanceiro.substring(0, 3);
      props.pedidoCompra.detalhesPagamentos.forEach((d) => {
        if (state.detalhePagamento.tipoDocumento === d.tipoDocumento) {
          qtdParcela += 1;
        }
      });
      if (props.pedidoCompra.numeroPedido > 0) {
        numero = `P${props.pedidoCompra.numeroPedido}${sigla}${qtdParcela + 1}`;
      } else {
        numero = `P${UtilitarioData.aplicaFormatoPersonalizadoData(props.pedidoCompra.dataCriacao, 'DDMMYYYYHHmm')}${sigla}${qtdParcela + 1}`;
      }
      return numero;
    }

    function preencherParcelasDisponiveis() {
      state.parcelasDisponiveis = [] as IOption[];
      if (state.tipoDocumentoSelecionado.parcelamentos.length === 0) {
        state.parcelasDisponiveis.push({ label: '1 Parcela', value: 1 });
        return;
      }
      state.tipoDocumentoSelecionado.parcelamentos.forEach((p, index) => {
        const item = {} as IOption;
        if (index === 0) {
          item.label = '1 Parcela';
        } else {
          item.label = `${index + 1} Parcelas`;
        }
        item.value = index + 1;
        state.parcelasDisponiveis.push(item);
      });
    }

    async function alterarTipoDocumento() {
      if ((state.detalhePagamento.codigo !== undefined && state.detalhePagamento.codigo > 0) || state.detalhePagamento.alterado) {
        return;
      }

      if (state.detalhePagamento.tipoDocumento > 0 && state.detalhePagamento.tipoDocumento !== undefined) {
        const tiposDocumentosFinanceiro = await servicoFormaPagamento.obterTiposDocumentos(props.pedidoCompra.codigoFormaPagamento, props.pedidoCompra.codigoEmpresa);
        const indexTipo = tiposDocumentosFinanceiro.findIndex((t) => t.codigoTipoDocumentoFinanceiro === state.detalhePagamento.tipoDocumento);
        state.tipoDocumentoSelecionado = tiposDocumentosFinanceiro[indexTipo];
        state.detalhePagamento.codigoPlanoConta = state.codigoPlanoConta;
        state.detalhePagamento.nomePlanoConta = state.nomePlanoConta;
        preencherParcelasDisponiveis();
        state.valorParcelado = state.valorRestante;
        state.detalhePagamento.valor = state.valorRestante;
        state.qtdParcelasSelecionada = 1;
      }
    }

    function calcularValorRestante() {
      let totalLancado = 0;

      props.pedidoCompra.detalhesPagamentos.forEach((d) => {
        totalLancado += d.valor;
      });
      state.valorRestante = (props.pedidoCompra.totalPedido) - totalLancado;
      if (state.valorRestante <= 0) {
        apresentarMensagemAlerta('Não há mais valor para ser pago!');
        modalBase.computedVisivel = false;
      }
    }

    function gerarParcelas() {
      if (UtilitarioGeral.validaLista(state.tipoDocumentoSelecionado.parcelamentos)) {
        const parcelamento = state.tipoDocumentoSelecionado.parcelamentos[state.qtdParcelasSelecionada - 1];
        const parcelas = UtilitarioFinanceiro.calcularParcelas(state.valorParcelado, state.qtdParcelasSelecionada);
        for (let i = 0; i <= state.qtdParcelasSelecionada - 1; i += 1) {
          const parcela = {} as IPedidoCompraDetalhePagamento;
          parcela.numeroTitulo = gerarNumeroTitulo();
          parcela.parcela = i + 1;
          parcela.dataVencimento = UtilitarioData.adicionarDiaNaData(props.pedidoCompra.dataCriacao, parcelamento.parcelas[i].prazo);
          parcela.valor = parcelas[i].valueOf();
          parcela.tipoDocumento = state.tipoDocumentoSelecionado.codigoTipoDocumentoFinanceiro;
          parcela.descricaoTipoDocumento = state.tipoDocumentoSelecionado.descricaoTipoDocumentoFinanceiro;
          parcela.conta = state.tipoDocumentoSelecionado.codigoConta;
          parcela.descricaoConta = state.tipoDocumentoSelecionado.descricaoConta;
          parcela.codigoPlanoConta = state.codigoPlanoConta;
          parcela.nomePlanoConta = state.nomePlanoConta;
          parcela.alterado = true;
          computedPedidoCompra.value.detalhesPagamentos.push(parcela);
        }
      } else {
        const detalhePagamentoSemParcela = {} as IPedidoCompraDetalhePagamento;
        detalhePagamentoSemParcela.numeroTitulo = gerarNumeroTitulo();
        detalhePagamentoSemParcela.parcela = 1;
        detalhePagamentoSemParcela.dataVencimento = props.pedidoCompra.dataCriacao;
        detalhePagamentoSemParcela.valor = state.valorParcelado;
        detalhePagamentoSemParcela.tipoDocumento = state.tipoDocumentoSelecionado.codigoTipoDocumentoFinanceiro;
        detalhePagamentoSemParcela.descricaoTipoDocumento = state.tipoDocumentoSelecionado.descricaoTipoDocumentoFinanceiro;
        detalhePagamentoSemParcela.conta = state.tipoDocumentoSelecionado.codigoConta;
        detalhePagamentoSemParcela.descricaoConta = state.tipoDocumentoSelecionado.descricaoConta;
        detalhePagamentoSemParcela.codigoPlanoConta = state.codigoPlanoConta;
        detalhePagamentoSemParcela.nomePlanoConta = state.nomePlanoConta;
        detalhePagamentoSemParcela.alterado = true;
        computedPedidoCompra.value.detalhesPagamentos.push(detalhePagamentoSemParcela);
      }
      emit('confirmar');
    }

    function validarCampos() {
      if (state.tipoDocumentoSelecionado.gerarCarteira && !state.detalhePagamento.alterado) {
        if (state.valorParcelado === 0 || state.valorParcelado === undefined) {
          apresentarMensagemAlerta('O valor parcelado deve ser informado!');
          return false;
        }

        if (state.qtdParcelasSelecionada === 0 || state.qtdParcelasSelecionada === undefined) {
          apresentarMensagemAlerta('A quantidade de parcelas deve ser informada!');
          return false;
        }

        if (state.tipoDocumentoSelecionado.valorMinimo > 0 && UtilitarioMatematico.arredondarValor(state.valorParcelado, 2) < UtilitarioMatematico.arredondarValor(state.tipoDocumentoSelecionado.valorMinimo, 2)) {
          apresentarMensagemAlerta(`O valor mínimo parcelado para esse tipo de documento é ${UtilitarioMascara.mascararValor(state.tipoDocumentoSelecionado.valorMinimo, 2)}!`);
          return false;
        }

        if (state.tipoDocumentoSelecionado.valorMaximo > 0 && UtilitarioMatematico.arredondarValor(state.valorParcelado, 2) > UtilitarioMatematico.arredondarValor(state.tipoDocumentoSelecionado.valorMaximo, 2)) {
          apresentarMensagemAlerta(`O valor máximo parcelado para esse tipo de documento é ${UtilitarioMascara.mascararValor(state.tipoDocumentoSelecionado.valorMaximo, 2)}!`);
          return false;
        }

        const valorParcela = state.valorParcelado / state.qtdParcelasSelecionada;
        if (state.tipoDocumentoSelecionado.valorMinParcela > 0 && UtilitarioMatematico.arredondarValor(valorParcela, 2) < UtilitarioMatematico.arredondarValor(state.tipoDocumentoSelecionado.valorMinParcela, 2)) {
          apresentarMensagemAlerta(`O valor mínimo da parcela para esse tipo de documento é ${UtilitarioMascara.mascararValor(state.tipoDocumentoSelecionado.valorMinParcela, 2)}!`);
          return false;
        }
      }

      return true;
    }

    function salvar() {
      const retornoValidacao = validarCampos();
      if (!retornoValidacao) return;

      if (state.detalhePagamento.alterado) {
        const indexDetalhePagamento = computedPedidoCompra.value.detalhesPagamentos.findIndex((d) => d.numeroTitulo === state.detalhePagamento.numeroTitulo);
        computedPedidoCompra.value.detalhesPagamentos[indexDetalhePagamento] = state.detalhePagamento;
        emit('confirmar');
      } else if (state.tipoDocumentoSelecionado.gerarCarteira) {
        gerarParcelas();
      } else {
        state.detalhePagamento.alterado = true;
        computedPedidoCompra.value.detalhesPagamentos.push(state.detalhePagamento);
        emit('confirmar');
      }
      modalBase.computedVisivel = false;
    }

    function cancelar() {
      modalBase.computedVisivel = false;
    }

    watch(async () => modalBase.computedVisivel, async () => {
      state.telaMobile = UtilitarioDispositivo.larguraTelaMobile();
      limparTela();
      telaBase.carregando = true;
      if (modalBase.computedVisivel) {
        if ((props.detalhePagamento.codigo !== undefined && props.detalhePagamento.codigo > 0) || props.detalhePagamento.alterado) {
          state.detalhePagamento = UtilitarioGeral.clonarObjeto(props.detalhePagamento);
          state.detalhePagamento.alterado = true;
        } else {
          calcularValorRestante();
        }
      }
      telaBase.carregando = false;
    });

    return {
      props,
      state,
      requisicaoAutorizacaoBase,
      telaBase,
      modalBase,
      computedPedidoCompra,
      UtilitarioGeral,
      alterarTipoDocumento,
      salvar,
      cancelar,
    };
  },
});
